import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, LayoutGrid, Clock, Maximize2, ExternalLink, Pencil, Save, RotateCcw, X } from "lucide-react";
import { scenes } from "./scenes";
import { speakerNotes } from "./scenes/notes";
import { postSync, subscribeSync } from "./sync";
import useKeyboard from "./hooks/useKeyboard";

const NOTES_OVERRIDE_KEY = "niche-keynote-notes-overrides-v1";

/** Read all locally-edited note overrides keyed by scene id. */
function readOverrides() {
  try {
    return JSON.parse(window.localStorage.getItem(NOTES_OVERRIDE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeOverrides(map) {
  try {
    window.localStorage.setItem(NOTES_OVERRIDE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/**
 * Presenter View — opens in a separate browser window.
 * Drag this window to your laptop screen while the audience sees the
 * main presentation (`/`) on the projector. Both windows stay in sync.
 *
 * Layout:
 *   ┌──────────────────────┬──────────────────────────┐
 *   │  Current slide       │  Speaker notes / script  │
 *   │  preview (live)      │  (scrollable)            │
 *   ├──────────────────────┤                          │
 *   │  Next-up preview     │                          │
 *   ├──────────────────────┴──────────────────────────┤
 *   │  Timer · controls · counter                      │
 *   └──────────────────────────────────────────────────┘
 */
export default function PresenterView() {
  const [index, setIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(true);

  const total = scenes.length;
  const current = scenes[index];
  const next = scenes[index + 1];
  const Current = current?.component;
  const Next = next?.component;

  /**
   * Local advance guard for the live preview shown inside this window.
   * Scenes with progressive disclosure (e.g. quiz-question, leads-me-to,
   * 12-trillion, package-table, video-critical, guess-ai-cost) wire
   * themselves into this ref. Pressing → in the presenter calls the local
   * guard so the preview's internal stage progresses, AND broadcasts a
   * STEP so the main window's scene advances its own (independent) state
   * in lock-step. This way the two windows stay visually in sync even
   * across in-slide stage transitions.
   */
  const advanceGuardRef = useRef(null);

  // Reset the guard whenever the slide changes — each scene re-registers on mount.
  useEffect(() => {
    advanceGuardRef.current = null;
  }, [index]);

  // Ask other windows for current state on mount, then listen for updates
  useEffect(() => {
    const unsub = subscribeSync((msg) => {
      if (msg?.type === "STATE" && typeof msg.index === "number") {
        setIndex(msg.index);
      }
    });
    postSync({ type: "REQUEST_STATE" });
    return unsub;
  }, []);

  // Local nav handlers — full standalone controller. We advance our own
  // index AND broadcast a GO so the main window stays in sync. No
  // in-slide stage logic any more — every "stage" is now a real slide.
  const handleNext = () => {
    if (index < total - 1) {
      const nextIdx = index + 1;
      setIndex(nextIdx);
      postSync({ type: "GO", index: nextIdx });
    }
  };
  const handlePrev = () => {
    if (index > 0) {
      const prevIdx = index - 1;
      setIndex(prevIdx);
      postSync({ type: "GO", index: prevIdx });
    }
  };
  const handleFirst = () => {
    setIndex(0);
    postSync({ type: "GO", index: 0 });
  };
  const handleLast = () => {
    setIndex(total - 1);
    postSync({ type: "GO", index: total - 1 });
  };

  useKeyboard({
    onNext: handleNext,
    onPrev: handlePrev,
    onFirst: handleFirst,
    onLast: handleLast,
  });

  // Timer
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const timer = useMemo(() => {
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [elapsed]);

  const noteText = speakerNotes[current?.id] || "";

  // Local edit-mode for the speaker notes — persisted in localStorage so
  // saved scripts survive a refresh (and are inherited by the main window if
  // it's ever wired through). Reset reverts to the default note.
  const [overrides, setOverrides] = useState(() => readOverrides());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  // Re-read overrides when the user re-focuses the tab — keeps things synced
  // if they edit on another window of the same origin.
  useEffect(() => {
    const onFocus = () => setOverrides(readOverrides());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // When switching slides, exit edit mode automatically.
  useEffect(() => {
    setEditing(false);
  }, [index]);

  const effectiveNoteText =
    (current && overrides[current.id] != null ? overrides[current.id] : noteText) || "";
  const hasOverride = !!(current && overrides[current.id] != null);

  const startEditing = () => {
    setDraft(effectiveNoteText);
    setEditing(true);
  };
  const cancelEditing = () => {
    setEditing(false);
    setDraft("");
  };
  const saveEditing = () => {
    if (!current) return;
    const next = { ...overrides, [current.id]: draft };
    setOverrides(next);
    writeOverrides(next);
    setEditing(false);
  };
  const resetNote = () => {
    if (!current) return;
    const next = { ...overrides };
    delete next[current.id];
    setOverrides(next);
    writeOverrides(next);
    setEditing(false);
  };

  return (
    <div
      className="fixed inset-0 bg-[#070b09] text-white overflow-hidden flex flex-col"
      data-testid="presenter-view"
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-8 py-5 border-b border-white/10">
        <div className="flex items-center gap-4">
          <span className="kn-eyebrow text-white/70">Presenter View</span>
          <span className="text-white/30">·</span>
          <span className="text-sm text-white/65 tracking-[0.18em] uppercase">
            {current?.title}
          </span>
        </div>
        <div className="flex items-center gap-6">
          <div
            className="flex items-center gap-2 text-white/85 tnum"
            data-testid="presenter-timer"
          >
            <Clock className="w-4 h-4" />
            <span className="tabular-nums text-base font-medium">{timer}</span>
            <button
              type="button"
              onClick={() => setRunning((r) => !r)}
              className="ml-2 text-[10px] tracking-[0.28em] uppercase text-white/50 hover:text-white/90"
              data-testid="presenter-timer-toggle"
            >
              {running ? "pause" : "resume"}
            </button>
            <button
              type="button"
              onClick={() => setElapsed(0)}
              className="text-[10px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80"
              data-testid="presenter-timer-reset"
            >
              reset
            </button>
          </div>
          <span
            className="text-base tracking-[0.18em] uppercase tnum text-white/85"
            data-testid="presenter-counter"
          >
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 overflow-hidden">
        {/* Left column — current + next previews */}
        <section className="lg:col-span-7 flex flex-col gap-6 min-h-0">
          {/* Current slide preview */}
          <div className="flex-1 min-h-0 rounded-2xl overflow-hidden border border-white/10 bg-black relative">
            <div
              className="absolute top-1/2 left-1/2 pointer-events-none"
              style={{
                width: "1920px",
                height: "1080px",
                transform: "translate(-50%, -50%) scale(var(--preview-scale, 0.42))",
              }}
            >
              {Current ? <Current advanceGuardRef={advanceGuardRef} {...(current?.props || {})} /> : null}
            </div>
            <div className="absolute top-3 left-3 text-[10px] tracking-[0.32em] uppercase text-white/85 bg-black/60 backdrop-blur px-2.5 py-1 rounded z-10">
              Now · {String(index + 1).padStart(2, "0")}
            </div>
            <PreviewScale />
          </div>

          {/* Next slide preview */}
          <div className="h-[28%] rounded-2xl overflow-hidden border border-white/10 bg-black relative">
            {Next ? (
              <div
                className="absolute top-1/2 left-1/2 pointer-events-none opacity-80"
                style={{
                  width: "1920px",
                  height: "1080px",
                  transform: "translate(-50%, -50%) scale(var(--next-scale, 0.22))",
                  filter: "saturate(0.9)",
                }}
              >
                <Next {...(next?.props || {})} />
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/30 text-2xl tracking-[0.3em] uppercase">
                End of deck
              </div>
            )}
            <div className="absolute top-3 left-3 text-[10px] tracking-[0.32em] uppercase text-white/70 bg-black/60 backdrop-blur px-2.5 py-1 rounded z-10">
              {next ? `Next · ${next.title}` : "End of deck"}
            </div>
            <NextPreviewScale />
          </div>
        </section>

        {/* Right column — speaker notes */}
        <section className="lg:col-span-5 rounded-2xl border border-white/10 bg-white/[0.03] flex flex-col min-h-0">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="kn-eyebrow text-white/65">Script · Speaker notes</span>
              {hasOverride && (
                <span
                  className="text-[10px] tracking-[0.32em] uppercase px-2 py-0.5 rounded-full"
                  style={{
                    color: "#96C11F",
                    background: "rgba(150,193,31,0.10)",
                    border: "1px solid rgba(150,193,31,0.40)",
                  }}
                  data-testid="presenter-notes-edited-badge"
                >
                  Edited
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {editing ? (
                <>
                  <NotesBtn
                    onClick={cancelEditing}
                    ariaLabel="Cancel editing"
                    testId="presenter-notes-cancel"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span>Cancel</span>
                  </NotesBtn>
                  <NotesBtn
                    onClick={saveEditing}
                    ariaLabel="Save script"
                    testId="presenter-notes-save"
                    primary
                  >
                    <Save className="w-3.5 h-3.5" />
                    <span>Save</span>
                  </NotesBtn>
                </>
              ) : (
                <>
                  {hasOverride && (
                    <NotesBtn
                      onClick={resetNote}
                      ariaLabel="Reset to default"
                      testId="presenter-notes-reset"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Reset</span>
                    </NotesBtn>
                  )}
                  <NotesBtn
                    onClick={startEditing}
                    ariaLabel="Edit script"
                    testId="presenter-notes-edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    <span>Edit</span>
                  </NotesBtn>
                </>
              )}
            </div>
          </div>

          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="presenter-grid flex-1 overflow-y-auto px-7 py-7 leading-[1.7] outline-none resize-none bg-transparent text-[16px] text-white/90 font-mono"
              spellCheck={false}
              data-testid="presenter-notes-textarea"
              placeholder={`Paste your script for "${current?.title || "this slide"}" here.\n\nMarkdown supported: **bold**, lists (- / 1.), \`code\`, > quotes.`}
            />
          ) : (
            <div
              className="presenter-grid flex-1 overflow-y-auto px-7 py-7 leading-[1.7]"
              data-testid="presenter-notes"
            >
              {effectiveNoteText ? (
                <Markdown text={effectiveNoteText} />
              ) : (
                <p className="text-white/45 italic">
                  No notes for this scene yet. Press{" "}
                  <span className="text-white/80">Edit</span> on the right to write
                  your script for "{current?.title}". Saved locally — survives a
                  refresh.
                </p>
              )}
            </div>
          )}
        </section>
      </main>

      {/* Bottom controls */}
      <footer className="border-t border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FooterBtn onClick={handlePrev} ariaLabel="Previous slide" testId="presenter-prev">
            <ChevronLeft className="w-5 h-5" />
            <span className="text-xs tracking-[0.28em] uppercase">Prev</span>
          </FooterBtn>
          <FooterBtn onClick={handleNext} ariaLabel="Next slide" testId="presenter-next" primary>
            <span className="text-xs tracking-[0.28em] uppercase">Next</span>
            <ChevronRight className="w-5 h-5" />
          </FooterBtn>
        </div>
        <div className="text-[11px] tracking-[0.28em] uppercase text-white/45 hidden md:block">
          ← prev · → next · home/end · in sync with main window
        </div>
        <div className="flex items-center gap-2">
          <FooterBtn
            onClick={() => postSync({ type: "TOGGLE_PRESENTER" })}
            ariaLabel="Open overview on main"
            testId="presenter-overview"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="text-xs tracking-[0.28em] uppercase">Overview</span>
          </FooterBtn>
          <FooterBtn
            onClick={() => window.open("/", "keynote-main", "popup,width=1600,height=900")}
            ariaLabel="Open main window"
            testId="presenter-open-main"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="text-xs tracking-[0.28em] uppercase">Main</span>
          </FooterBtn>
          <FooterBtn
            onClick={async () => {
              try {
                if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
                else await document.exitFullscreen();
              } catch {
                /* ignore */
              }
            }}
            ariaLabel="Fullscreen"
            testId="presenter-fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </FooterBtn>
        </div>
      </footer>
    </div>
  );
}

const FooterBtn = ({ children, onClick, ariaLabel, testId, primary = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 transition-colors ${
      primary
        ? "btn-lime"
        : "bg-white/5 border border-white/12 text-white/85 hover:bg-white/10 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const NotesBtn = ({ children, onClick, ariaLabel, testId, primary = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase transition-colors ${
      primary
        ? "bg-lime text-black hover:brightness-110"
        : "bg-white/5 border border-white/15 text-white/80 hover:bg-white/12 hover:text-white"
    }`}
    style={primary ? { background: "#96C11F", color: "#0d0d0f", fontWeight: 700 } : undefined}
  >
    {children}
  </button>
);

/* ─── Helpers ─────────────────────────────────────────────────────────── */

// Compute a scale that fits the 1920×1080 preview into its container.
function PreviewScale() {
  useEffect(() => {
    const compute = () => {
      const root = document.querySelector("[data-testid='presenter-view']");
      if (!root) return;
      const main = root.querySelector("main");
      if (!main) return;
      const left = main.querySelector("section");
      if (!left) return;
      const cur = left.firstChild;
      if (!cur) return;
      const inner = cur.querySelector("[style*='1920px']");
      if (!inner) return;
      const rect = cur.getBoundingClientRect();
      const scale = Math.min(rect.width / 1920, rect.height / 1080);
      inner.style.setProperty("--preview-scale", String(scale));
    };
    compute();
    window.addEventListener("resize", compute);
    const id = setInterval(compute, 800);
    return () => {
      window.removeEventListener("resize", compute);
      clearInterval(id);
    };
  }, []);
  return null;
}

function NextPreviewScale() {
  useEffect(() => {
    const compute = () => {
      const root = document.querySelector("[data-testid='presenter-view']");
      if (!root) return;
      const main = root.querySelector("main");
      if (!main) return;
      const left = main.querySelector("section");
      if (!left) return;
      const nx = left.children[1];
      if (!nx) return;
      const inner = nx.querySelector("[style*='1920px']");
      if (!inner) return;
      const rect = nx.getBoundingClientRect();
      const scale = Math.min(rect.width / 1920, rect.height / 1080);
      inner.style.setProperty("--next-scale", String(scale));
    };
    compute();
    window.addEventListener("resize", compute);
    const id = setInterval(compute, 800);
    return () => {
      window.removeEventListener("resize", compute);
      clearInterval(id);
    };
  }, []);
  return null;
}

/**
 * Tiny markdown renderer for speaker notes.
 * Supports: **bold**, paragraphs (\n\n), unordered (- / *) and ordered (1.) lists,
 * `inline code`. Intentionally simple — no external deps.
 */
function Markdown({ text }) {
  const blocks = text.split(/\n{2,}/);
  return (
    <div className="space-y-5 text-[17px] text-white/85">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        const ulMatch = lines.every((l) => /^\s*[-*]\s+/.test(l));
        const olMatch = lines.every((l) => /^\s*\d+\.\s+/.test(l));
        if (ulMatch) {
          return (
            <ul key={i} className="list-disc pl-6 space-y-2 marker:text-lime">
              {lines.map((l, j) => (
                <li key={j}>
                  <Inline text={l.replace(/^\s*[-*]\s+/, "")} />
                </li>
              ))}
            </ul>
          );
        }
        if (olMatch) {
          return (
            <ol key={i} className="list-decimal pl-6 space-y-2 marker:text-lime marker:font-semibold">
              {lines.map((l, j) => (
                <li key={j}>
                  <Inline text={l.replace(/^\s*\d+\.\s+/, "")} />
                </li>
              ))}
            </ol>
          );
        }
        return (
          <p key={i}>
            <Inline text={block} />
          </p>
        );
      })}
    </div>
  );
}

function Inline({ text }) {
  // Render **bold** and `code` while preserving line breaks within a paragraph.
  const parts = [];
  let rest = text;
  let key = 0;
  const regex = /(\*\*[^*]+\*\*|`[^`]+`|\n)/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(rest)) !== null) {
    if (m.index > lastIndex) parts.push(rest.slice(lastIndex, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(
        <strong key={key++} className="text-white">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("`")) {
      parts.push(
        <code key={key++} className="px-1.5 py-0.5 rounded bg-white/8 text-lime text-[14px]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok === "\n") {
      parts.push(<br key={key++} />);
    }
    lastIndex = m.index + tok.length;
  }
  if (lastIndex < rest.length) parts.push(rest.slice(lastIndex));
  return <>{parts}</>;
}
